"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { useTheme } from "next-themes"
import {
  Building2,
  Plus,
  User,
  Shield,
  Briefcase,
  Layers,
  ArrowRight,
  Search,
  LogOut,
  Moon,
  Sun,
  Languages,
  Sparkles,
  MapPin,
  FileText,
  Tag,
  ExternalLink,
  ChevronRight,
  Clock,
  Laptop,
  CheckCircle2,
  AlertCircle,
  X,
  Palette,
  Volume2,
  KeyRound,
  ShieldCheck,
  ShieldAlert,
  Crown,
  ShieldUser,
  FolderPlus,
  Loader2
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu"
import { SwitchAccountModal } from "@/components/auth/SwitchAccountModal"
import { ProfileView } from "@/components/profile/ProfileView"
import { translations, Language } from "@/lib/translations"
import { Account, UserProfile, getOldestActiveWorkspace } from "@/types/workspace"
import { toast } from "sonner"

export default function HomePage() {
  const router = useRouter()
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = React.useState(false)

  // Estados principais de dados
  const [user, setUser] = React.useState<UserProfile | null>(null)
  const [accounts, setAccounts] = React.useState<Account[]>([])
  const [accountProfiles, setAccountProfiles] = React.useState<any[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [searchQuery, setSearchQuery] = React.useState("")

  // Modais
  const [isSwitchAccountOpen, setIsSwitchAccountOpen] = React.useState(false)
  const [switchAccountInitialMode, setSwitchAccountInitialMode] = React.useState<"list" | "create">("create")
  const [isProfileModalOpen, setIsProfileModalOpen] = React.useState(false)
  const [profileTab, setProfileTab] = React.useState("profile")
  const [selectedAccountForNewWs, setSelectedAccountForNewWs] = React.useState<Account | null>(null)
  const [newWorkspaceName, setNewWorkspaceName] = React.useState("")
  const [isCreatingWorkspace, setIsCreatingWorkspace] = React.useState(false)
  const [showCreateWsModal, setShowCreateWsModal] = React.useState(false)

  // Estados do Perfil do Usuário
  const [profileName, setProfileName] = React.useState("")
  const [profilePhone, setProfilePhone] = React.useState("")
  const [profileAvatarUrl, setProfileAvatarUrl] = React.useState<string | null>(null)
  const [isLoginMusicEnabled, setIsLoginMusicEnabled] = React.useState(true)
  const [loginMusicUrl, setLoginMusicUrl] = React.useState("https://assets.mixkit.co/active_storage/sfx/2013/2013-200.wav")
  const [lockscreenPin, setLockscreenPin] = React.useState("")

  // Idioma
  const [lang, setLang] = React.useState<Language>("pt")
  const t = translations[lang]

  React.useEffect(() => {
    setMounted(true)
    const savedLang = localStorage.getItem("language") as Language
    if (savedLang && (savedLang === "pt" || savedLang === "en" || savedLang === "es")) {
      setLang(savedLang)
    }

    try {
      const savedUser = localStorage.getItem("user")
      if (savedUser) {
        const parsed = JSON.parse(savedUser)
        setUser(parsed)
        setProfileName(parsed.name || "")
        setProfilePhone(parsed.phone || "")
        setProfileAvatarUrl(parsed.avatarUrl ?? null)
        if (parsed.loginMusicUrl) setLoginMusicUrl(parsed.loginMusicUrl)
        if (parsed.loginMusicEnabled !== undefined) setIsLoginMusicEnabled(parsed.loginMusicEnabled)
        if (parsed.lockscreenPin) setLockscreenPin(parsed.lockscreenPin)
      }
    } catch (e) { }
  }, [])

  // Alteração de Senha
  const [currentPassword, setCurrentPassword] = React.useState("")
  const [newPassword, setNewPassword] = React.useState("")
  const [confirmPassword, setConfirmPassword] = React.useState("")
  const [isChangingPassword, setIsChangingPassword] = React.useState(false)

  // 2FA
  const [totpDisableCode, setTotpDisableCode] = React.useState("")
  const [totpCode, setTotpCode] = React.useState("")
  const [totpError, setTotpError] = React.useState<string | null>(null)
  const [setupData, setSetupData] = React.useState<{ qrCodeUrl?: string; secret?: string } | null>(null)

  // Saudação Dinâmica
  const [greeting, setGreeting] = React.useState("")
  React.useEffect(() => {
    const hour = new Date().getHours()
    if (hour >= 5 && hour < 12) {
      setGreeting(t.greetingMorning)
    } else if (hour >= 12 && hour < 18) {
      setGreeting(t.greetingAfternoon)
    } else {
      setGreeting(t.greetingEvening)
    }
  }, [lang, t])

  // Carregar dados do usuário e empresas
  const loadInitialData = React.useCallback(async (showLoader = true) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1] || (typeof window !== "undefined" ? localStorage.getItem("token") || localStorage.getItem("bold_token") : null)

    if (!token) {
      const search = typeof window !== "undefined" ? window.location.search : ""
      router.replace(`/login${search}`)
      return
    }

    try {
      if (showLoader) {
        setIsLoading(true)
      }
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query InitHome {
              me {
                id
                name
                email
                avatarUrl
                phone
                twoFactorEnabled
                loginMusicUrl
                loginMusicEnabled
                fullSizeScreen
                lockscreenPin
              }
              myAccounts {
                id
                name
                slug
                logoUrl
                bannerUrl
                document
                bio
                category
                location
                website
                status
                workspaces {
                  id
                  name
                  slug
                  status
                  sortOrder
                  createdAt
                  installedApps {
                    id
                    app {
                      id
                      name
                      iconUrl
                      iframeUrl
                      scopes
                      clientId
                    }
                  }
                }
              }
              accountProfiles {
                id
                userId
                accountId
                role
                status
              }
            }
          `
        })
      })

      const result = await res.json()

      if (result.errors && result.errors.some((e: any) => e.extensions?.code === "UNAUTHENTICATED")) {
        document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;"
        if (typeof localStorage !== "undefined") {
          localStorage.removeItem("token")
          localStorage.removeItem("bold_token")
          localStorage.removeItem("user")
          localStorage.removeItem("currentAccount")
        }
        const search = typeof window !== "undefined" ? window.location.search : ""
        router.replace(`/login${search}`)
        return
      }

      if (result.data) {
        const userData = result.data.me
        const accs = result.data.myAccounts || []
        const profs = result.data.accountProfiles || []

        setUser(userData)
        setAccounts(accs)
        setAccountProfiles(profs)

        if (userData) {
          setProfileName(userData.name || "")
          setProfileAvatarUrl(userData.avatarUrl ?? null)
          setProfilePhone(userData.phone ?? "")
          if (userData.loginMusicUrl) setLoginMusicUrl(userData.loginMusicUrl)
          if (userData.loginMusicEnabled !== undefined) setIsLoginMusicEnabled(userData.loginMusicEnabled)
          if (userData.lockscreenPin) setLockscreenPin(userData.lockscreenPin)
          localStorage.setItem("user", JSON.stringify(userData))
        }
      }
    } catch (err) {
      console.error("Erro ao carregar dados da página inicial:", err)
      toast.error(t.connectionErrorData)
    } finally {
      if (showLoader) {
        setIsLoading(false)
      }
    }
  }, [router, t.connectionErrorData])

  React.useEffect(() => {
    loadInitialData()
  }, [loadInitialData])

  // Logout
  const handleLogout = async () => {
    document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;"
    try {
      await fetch("/api/auth/logout", { method: "POST" })
    } catch (e) { }
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("token")
      localStorage.removeItem("bold_token")
      localStorage.removeItem("user")
      localStorage.removeItem("currentAccount")
    }
    router.push("/login")
  }

  // Acessar uma Empresa (Switch Account e Navegar)
  const handleEnterAccount = async (account: Account, targetWorkspaceSlug?: string) => {
    // Validar se o usuário possui perfil ativo na conta
    const prof = accountProfiles.find((p) => p.accountId === account.id && p.userId === user?.id)
    if (prof && prof.status === "inactive") {
      toast.error(t.accessDisabledToast)
      return
    }

    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1] || (typeof window !== "undefined" ? localStorage.getItem("token") : null)

    const oldestWs = getOldestActiveWorkspace(account.workspaces)
    const initialChosenWs = targetWorkspaceSlug
      ? (account.workspaces || []).find((w) => (w.slug?.toLowerCase() === targetWorkspaceSlug.toLowerCase() || w.id === targetWorkspaceSlug) && w.status !== false)
      : oldestWs

    if (!targetWorkspaceSlug && !initialChosenWs) {
      toast.error("Você não possui acesso a nenhum workspace ativo nesta empresa.")
      router.push("/")
      return
    }

    const targetUrl = initialChosenWs
      ? `/${account.slug}/${initialChosenWs.slug || initialChosenWs.id}`
      : targetWorkspaceSlug
        ? `/${account.slug}/${targetWorkspaceSlug}`
        : `/${account.slug}`

    if (!token) {
      router.push("/login")
      return
    }

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation SwitchAccount($accountId: ID!) {
              switchAccount(accountId: $accountId) {
                token
                currentAccount {
                  id
                  name
                  slug
                  logoUrl
                  workspaces {
                    id
                    name
                    slug
                    status
                    sortOrder
                    createdAt
                  }
                }
              }
            }
          `,
          variables: { accountId: account.id }
        })
      })

      const data = await res.json()
      if (data.data?.switchAccount) {
        const { token: newToken, currentAccount: switchedAcc } = data.data.switchAccount
        document.cookie = `token=${newToken}; path=/; max-age=604800; SameSite=Lax` + (location.protocol === "https:" ? "; Secure" : "")
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("token", newToken)
          localStorage.setItem("bold_token", newToken)
          localStorage.setItem("currentAccount", JSON.stringify(switchedAcc))
        }

        const serverWorkspaces = switchedAcc.workspaces || account.workspaces
        const serverChosenWs = targetWorkspaceSlug
          ? (serverWorkspaces || []).find((w: any) => (w.slug?.toLowerCase() === targetWorkspaceSlug.toLowerCase() || w.id === targetWorkspaceSlug) && w.status !== false)
          : getOldestActiveWorkspace(serverWorkspaces)

        if (!serverChosenWs) {
          toast.error("Você não possui acesso a nenhum workspace ativo nesta empresa.")
          router.push("/")
          return
        }

        router.push(`/${switchedAcc.slug}/${serverChosenWs.slug || serverChosenWs.id}`)
      } else {
        // Fallback caso já esteja no token correto
        localStorage.setItem("currentAccount", JSON.stringify(account))
        router.push(targetUrl)
      }
    } catch (e) {
      console.error("Erro ao alternar para a empresa selecionada:", e)
      router.push(targetUrl)
    }
  }

  // Criar Workspace para Empresa sem Workspaces
  const handleCreateWorkspaceForAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newWorkspaceName.trim() || !selectedAccountForNewWs) return

    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1] || (typeof window !== "undefined" ? localStorage.getItem("token") : null)

    if (!token) {
      router.push("/login")
      return
    }

    setIsCreatingWorkspace(true)
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation CreateWorkspace($name: String!, $accountId: ID!) {
              createWorkspace(name: $name, accountId: $accountId) {
                id
                name
                slug
                sortOrder
              }
            }
          `,
          variables: {
            name: newWorkspaceName.trim(),
            accountId: selectedAccountForNewWs.id
          }
        })
      })

      const data = await res.json()
      if (data.errors && data.errors.length > 0) {
        toast.error(data.errors[0].message || "Erro ao criar workspace.")
        return
      }

      if (data.data?.createWorkspace) {
        const createdWs = data.data.createWorkspace
        toast.success(`Workspace "${createdWs.name}" criado com sucesso!`)

        // Atualizar lista de contas no estado local
        setAccounts((prev) =>
          prev.map((acc) =>
            acc.id === selectedAccountForNewWs.id
              ? { ...acc, workspaces: [...(acc.workspaces || []), createdWs] }
              : acc
          )
        )

        setShowCreateWsModal(false)
        setSelectedAccountForNewWs(null)
        setNewWorkspaceName("")
      }
    } catch (err: any) {
      console.error("Erro ao criar workspace:", err)
      toast.error(err.message || "Erro ao criar workspace.")
    } finally {
      setIsCreatingWorkspace(false)
    }
  }

  // Salvar Perfil
  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1] || localStorage.getItem("token")
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation UpdateProfile(
              $name: String
              $avatarUrl: String
              $phone: String
              $loginMusicUrl: String
              $loginMusicEnabled: Boolean
              $lockscreenPin: String
            ) {
              updateProfile(
                name: $name
                avatarUrl: $avatarUrl
                phone: $phone
                loginMusicUrl: $loginMusicUrl
                loginMusicEnabled: $loginMusicEnabled
                lockscreenPin: $lockscreenPin
              ) {
                id
                name
                avatarUrl
                phone
                loginMusicUrl
                loginMusicEnabled
                lockscreenPin
              }
            }
          `,
          variables: {
            name: profileName,
            avatarUrl: profileAvatarUrl,
            phone: profilePhone,
            loginMusicUrl,
            loginMusicEnabled: isLoginMusicEnabled,
            lockscreenPin
          }
        })
      })

      const result = await res.json()
      if (result.errors && result.errors.length > 0) {
        toast.error(t.profileUpdateError, { description: result.errors[0].message })
        return
      }

      if (result.data?.updateProfile) {
        setUser((prev) => prev ? { ...prev, ...result.data.updateProfile } : result.data.updateProfile)
        toast.success(t.profileUpdated, { description: t.profileUpdatedDesc })
        setIsProfileModalOpen(false)
      }
    } catch (err) {
      console.error(err)
      toast.error(t.profileSaveError)
    }
  }

  // Alterar Senha
  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t.fillAllPasswordFields)
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error(t.passwordsDontMatch)
      return
    }
    if (newPassword.length < 8) {
      toast.error(t.passwordMinLength)
      return
    }

    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1] || localStorage.getItem("token")
    if (!token) return

    setIsChangingPassword(true)
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
              changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
            }
          `,
          variables: { currentPassword, newPassword }
        })
      })
      const result = await res.json()
      if (result.errors && result.errors.length > 0) {
        toast.error(t.profileUpdateError, { description: result.errors[0].message })
      } else {
        toast.success(t.passwordChangedSuccess)
        setCurrentPassword("")
        setNewPassword("")
        setConfirmPassword("")
      }
    } catch (e) {
      toast.error(t.connectionErrorData)
    } finally {
      setIsChangingPassword(false)
    }
  }

  // Iniciar Setup do 2FA
  const handleStart2FASetup = async () => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1] || localStorage.getItem("token")
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation Setup2FA {
              setup2FA {
                qrCodeUrl
                secret
              }
            }
          `
        })
      })
      const result = await res.json()
      if (result.data?.setup2FA) {
        setSetupData(result.data.setup2FA)
        setTotpError(null)
      }
    } catch (e) {
      toast.error(t.twoFactorSetupError)
    }
  }

  // Confirmar e Ativar 2FA
  const handleEnable2FA = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!totpCode || totpCode.length !== 6) {
      setTotpError(t.enterSixDigitCode)
      return
    }

    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1] || localStorage.getItem("token")
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation Enable2FA($code: String!) {
              enable2FA(code: $code)
            }
          `,
          variables: { code: totpCode }
        })
      })
      const result = await res.json()
      if (result.errors && result.errors.length > 0) {
        setTotpError(result.errors[0].message)
      } else {
        toast.success(t.twoFactorEnabledSuccess)
        setUser((prev) => prev ? { ...prev, twoFactorEnabled: true } : null)
        setSetupData(null)
        setTotpCode("")
        setTotpError(null)
      }
    } catch (e) {
      setTotpError(t.twoFactorActivateError)
    }
  }

  // Desativar 2FA
  const handleDisable2FA = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!totpDisableCode) {
      toast.error(t.enter2faOrRecovery)
      return
    }

    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1] || localStorage.getItem("token")
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation Disable2FA($code: String!) {
              disable2FA(code: $code)
            }
          `,
          variables: { code: totpDisableCode }
        })
      })
      const result = await res.json()
      if (result.errors && result.errors.length > 0) {
        toast.error(t.twoFactorDisableError, { description: result.errors[0].message })
      } else {
        toast.success(t.twoFactorDisabledSuccess)
        setUser((prev) => prev ? { ...prev, twoFactorEnabled: false } : null)
        setTotpDisableCode("")
      }
    } catch (e) {
      toast.error(t.twoFactorDisableError)
    }
  }

  // Filtrar apenas empresas onde o usuário possui perfil ativo e a conta está ativa
  const activeAccounts = React.useMemo(() => {
    return accounts.filter((acc) => {
      if (acc.status && acc.status !== "active") return false
      const prof = accountProfiles.find((p) => p.accountId === acc.id && p.userId === user?.id)
      if (prof && prof.status === "inactive") return false
      return true
    })
  }, [accounts, accountProfiles, user?.id])

  // Filtragem de empresas por busca
  const filteredAccounts = React.useMemo(() => {
    if (!searchQuery.trim()) return activeAccounts
    const q = searchQuery.toLowerCase()
    return activeAccounts.filter(
      (acc) =>
        acc.name.toLowerCase().includes(q) ||
        acc.slug.toLowerCase().includes(q) ||
        (acc.category && acc.category.toLowerCase().includes(q)) ||
        (acc.location && acc.location.toLowerCase().includes(q)) ||
        (acc.document && acc.document.includes(q))
    )
  }, [activeAccounts, searchQuery])

  // Total de Workspaces em todas as empresas
  const totalWorkspaces = React.useMemo(() => {
    return activeAccounts.reduce((acc, curr) => acc + ((curr.workspaces || []).filter((w) => w.status !== false).length), 0)
  }, [activeAccounts])

  // Obter Role do usuário na empresa
  const getUserRoleInAccount = (accountId: string) => {
    const prof = accountProfiles.find((p) => p.accountId === accountId && p.userId === user?.id)
    if (!prof) return "member"
    return prof.role
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "owner":
        return { label: t.roleOwner, icon: Crown, color: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" }
      case "admin":
        return { label: t.roleAdmin, icon: ShieldUser, color: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" }
      case "manager":
        return { label: t.roleManager || "Gerente", icon: ShieldCheck, color: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20" }
      default:
        return { label: t.roleMember, icon: User, color: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20" }
    }
  }

  if (isLoading) {
    const loadingText = lang === "en" ? "Loading" : lang === "es" ? "Cargando" : "Carregando"
    const words = lang === "en"
      ? ["accounts", "workspaces", "settings", "profile", "accounts"]
      : lang === "es"
        ? ["empresas", "workspaces", "configuraciones", "perfil", "empresas"]
        : ["empresas", "workspaces", "configurações", "perfil", "empresas"]

    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-950 transition-colors duration-300">
        <div className="card-loader select-none">
          <div className="custom-loader">
            <p className="text-[#004EB1] font-sans font-semibold">{loadingText}</p>
            <div className="words select-none pointer-events-none">
              {words.map((word, idx) => (
                <span key={idx} className="word font-sans">
                  {word}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen w-full bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 font-sans flex flex-col selection:bg-[#004EB1]/20">

      {/* 1. Barra de Navegação Superior */}
      <header className="sticky top-0 z-40 h-16 w-full border-b border-zinc-200/80 dark:border-zinc-800/80 bg-white/70 dark:bg-zinc-950/70 backdrop-blur-xl px-4 sm:px-8 flex items-center justify-between transition-all">
        {/* Lado Esquerdo: Logo & Branding */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2.5 cursor-pointer select-none group" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src="/logo.png" alt="Bold" className="size-9 rounded-xl object-cover shadow-sm group-hover:scale-105 transition-transform" />
            <div className="flex flex-col">
              <span className="font-extrabold text-base tracking-tight text-zinc-900 dark:text-white leading-tight">
                Bold Workspace
              </span>
              <span className="text-[10px] text-zinc-400 dark:text-zinc-500 font-medium tracking-wide">
                {t.centralPortal}
              </span>
            </div>
          </div>
        </div>

        {/* Lado Direito: Ações & Perfil */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Seletor de Idioma */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center h-9 px-2.5 rounded-xl border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-xs font-semibold gap-1.5 cursor-pointer transition-colors outline-none">
              <Languages className="size-3.5 text-[#004EB1] dark:text-blue-400" />
              <span className="uppercase">{lang}</span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36 font-sans">
              <DropdownMenuItem onClick={() => { setLang("pt"); localStorage.setItem("language", "pt"); }} className={`cursor-pointer text-xs ${lang === "pt" ? "font-bold text-[#004EB1]" : ""}`}>
                🇧🇷 Português
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setLang("en"); localStorage.setItem("language", "en"); }} className={`cursor-pointer text-xs ${lang === "en" ? "font-bold text-[#004EB1]" : ""}`}>
                🇺🇸 English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setLang("es"); localStorage.setItem("language", "es"); }} className={`cursor-pointer text-xs ${lang === "es" ? "font-bold text-[#004EB1]" : ""}`}>
                🇪🇸 Español
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Modo Escuro */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="size-9 rounded-xl border border-zinc-200 dark:border-zinc-800 cursor-pointer"
            title={mounted ? (theme === "dark" ? t.lightModeLabel : t.darkModeLabel) : t.toggleModeLabel}
          >
            {mounted && theme === "dark" ? (
              <Sun className="size-4 text-amber-400" />
            ) : (
              <Moon className="size-4 text-purple-600" />
            )}
          </Button>

          {/* Menu do Usuário */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2 p-1 pl-1.5 pr-2.5 rounded-full border border-zinc-200 dark:border-zinc-800 bg-zinc-100/60 dark:bg-zinc-900/60 hover:bg-zinc-100 dark:hover:bg-zinc-850 transition-colors cursor-pointer outline-none">
              <div className="size-7 rounded-full bg-[#004EB1] overflow-hidden flex items-center justify-center font-bold text-white text-xs select-none shadow-xs">
                {mounted && user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt={user.name || "Avatar"} className="w-full h-full object-cover" />
                ) : mounted && user?.name ? (
                  user.name.substring(0, 2).toUpperCase()
                ) : mounted && user?.email ? (
                  user.email.substring(0, 2).toUpperCase()
                ) : (
                  <User className="size-3.5" />
                )}
              </div>
              <span className="text-xs font-semibold text-zinc-800 dark:text-zinc-200 hidden sm:inline max-w-[120px] truncate">
                {mounted && user?.name ? user.name : mounted && user?.email ? user.email : (isLoading ? "..." : "")}
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 mt-1 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-1 font-sans z-[99999]">
              <DropdownMenuLabel className="font-bold text-zinc-800 dark:text-zinc-200 text-xs px-2 py-1.5">
                {mounted && user?.name ? user.name : mounted && user?.email ? user.email : t.myAccount}
                {mounted && user?.email && <span className="block text-[10px] font-normal text-zinc-400 mt-0.5">{user.email}</span>}
              </DropdownMenuLabel>

              <DropdownMenuSeparator className="bg-zinc-100 dark:bg-zinc-800" />

              <DropdownMenuItem
                onClick={() => {
                  setIsProfileModalOpen(true)
                  setProfileTab("profile")
                }}
                className="cursor-pointer text-xs py-2 px-2.5 rounded-md focus:bg-zinc-50 dark:focus:bg-zinc-800 flex items-center gap-2.5 font-semibold text-zinc-800 dark:text-zinc-200"
              >
                <User className="size-4 text-[#004EB1] dark:text-blue-400 shrink-0" />
                <span>{t.myProfileTitle}</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => {
                  setSwitchAccountInitialMode("create")
                  setIsSwitchAccountOpen(true)
                }}
                className="cursor-pointer text-xs py-2 px-2.5 rounded-md focus:bg-zinc-50 dark:focus:bg-zinc-800 flex items-center gap-2.5 font-semibold text-zinc-800 dark:text-zinc-200"
              >
                <Plus className="size-4 text-[#004EB1] dark:text-blue-400 shrink-0" />
                <span>{t.createCompany}</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="bg-zinc-100 dark:bg-zinc-800" />

              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 focus:bg-red-50 dark:focus:bg-red-950/20 cursor-pointer text-xs py-2 px-2.5 rounded-md flex items-center gap-2.5 font-semibold"
              >
                <LogOut className="size-4 shrink-0" />
                <span>{t.logoutTitle}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* 2. Conteúdo Principal */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-8">

        {/* Banner Hero / Boas-vindas */}
        <div className="relative overflow-hidden rounded-3xl border border-zinc-200/80 dark:border-zinc-800/80 bg-gradient-to-br from-blue-50/80 via-white to-indigo-50/50 dark:from-zinc-900/90 dark:via-zinc-900/60 dark:to-[#004EB1]/10 p-6 sm:p-10 shadow-xs">
          {/* Efeitos Decorativos de Fundo */}
          <div className="absolute top-0 right-0 -mt-8 -mr-8 size-64 rounded-full bg-[#004EB1]/10 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-1/3 -mb-12 size-48 rounded-full bg-blue-400/10 blur-2xl pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#004EB1]/10 dark:bg-blue-400/10 border border-[#004EB1]/20 text-[#004EB1] dark:text-blue-400 text-xs font-semibold">
                <Sparkles className="size-3.5" />
                <span>{mounted && user?.name ? `${greeting}, ${user.name.split(" ")[0]}!` : `${greeting}!`}</span>
              </div>
              <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-zinc-900 dark:text-white">
                {t.homeHeroTitle}
              </h1>
              <p className="text-sm sm:text-base text-zinc-600 dark:text-zinc-400 font-normal leading-relaxed">
                {t.homeHeroSubtitle}
              </p>
            </div>
          </div>
        </div>

        {/* 3. Seção: Minhas Empresas */}
        <div className="space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 dark:text-white flex items-center gap-2.5">
                <Building2 className="size-6 text-[#004EB1] dark:text-blue-400" />
                <span>{t.myCompanies}</span>
              </h2>
              <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
                {t.myCompaniesSubtitle}
              </p>
            </div>

            {/* Barra de Pesquisa */}
            {activeAccounts.length > 0 && (
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400 pointer-events-none" />
                <Input
                  placeholder={t.searchCompany}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-10 rounded-xl border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-xs font-sans"
                />
              </div>
            )}
          </div>

          {/* Grid de Empresas */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-zinc-100/50 dark:bg-zinc-900/50 animate-pulse" />
              ))}
            </div>
          ) : activeAccounts.length > 0 ? (
            filteredAccounts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAccounts.map((account) => {
                  const role = getUserRoleInAccount(account.id)
                  const roleBadge = getRoleBadge(role)
                  const workspaces = (account.workspaces || []).filter((w) => w.status !== false)

                  return (
                    <Card
                      key={account.id}
                      className="group relative overflow-hidden rounded-3xl border border-zinc-200/80 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-xs hover:shadow-xl hover:border-[#004EB1]/50 dark:hover:border-blue-500/50 transition-all duration-300 flex flex-col justify-between"
                    >
                      {/* Banner de Capa */}
                      <div className="relative h-24 w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 overflow-hidden">
                        {account.bannerUrl ? (
                          <img src={account.bannerUrl} alt={account.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-r from-[#004EB1] via-[#003d8d] to-indigo-700 opacity-90" />
                        )}

                        {/* Tag de Papel do Usuário */}
                        <div className="absolute top-3 right-3">
                          <span
                            title={roleBadge.label}
                            className={`inline-flex items-center justify-center size-7 rounded-full border backdrop-blur-md bg-white/90 dark:bg-zinc-950/90 ${roleBadge.color}`}
                          >
                            <roleBadge.icon className="size-3.5" />
                          </span>
                        </div>
                      </div>

                      {/* Conteúdo do Card */}
                      <div className="p-6 space-y-4 flex-1">
                        {/* Logo da Empresa & Informações Principais */}
                        <div className="flex items-start gap-4 -mt-10">
                          <div className="size-16 rounded-2xl border-4 border-white dark:border-zinc-900 bg-white dark:bg-zinc-950 shadow-md overflow-hidden flex items-center justify-center font-bold text-lg text-white bg-gradient-to-br from-[#004EB1] to-indigo-600 shrink-0 select-none">
                            {account.logoUrl ? (
                              <img src={account.logoUrl} alt={account.name} className="w-full h-full object-cover" />
                            ) : (
                              account.name.substring(0, 2).toUpperCase()
                            )}
                          </div>

                          <div className="min-w-0 pt-4">
                            <h3 className="font-extrabold text-lg text-zinc-900 dark:text-white truncate group-hover:text-[#004EB1] dark:group-hover:text-blue-400 transition-colors">
                              {account.name}
                            </h3>
                            <span className="inline-block text-xs text-zinc-400 dark:text-zinc-500 font-mono">
                              /{account.slug}
                            </span>
                          </div>
                        </div>

                        {/* Metadados: Categoria, Localização, Documento */}
                        <div className="space-y-1.5 text-xs text-zinc-500 dark:text-zinc-400 pt-1">
                          {account.category && (
                            <div className="flex items-center gap-2 truncate">
                              <Tag className="size-3.5 text-[#004EB1] shrink-0" />
                              <span className="truncate">{account.category}</span>
                            </div>
                          )}
                          {account.location && (
                            <div className="flex items-center gap-2 truncate">
                              <MapPin className="size-3.5 text-emerald-500 shrink-0" />
                              <span className="truncate">{account.location}</span>
                            </div>
                          )}
                          {account.document && (
                            <div className="flex items-center gap-2 truncate font-mono text-[11px]">
                              <FileText className="size-3.5 text-purple-500 shrink-0" />
                              <span className="truncate">{account.document}</span>
                            </div>
                          )}
                        </div>

                        {/* Lista de Workspaces Disponíveis ou Botão para Adicionar */}
                        <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800/80 mt-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                              <Layers className="size-3 text-[#004EB1] dark:text-blue-400" />
                              {t.workspacesLabel}
                            </span>
                            {(role === "owner" || role === "admin") && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedAccountForNewWs(account)
                                  setNewWorkspaceName("")
                                  setShowCreateWsModal(true)
                                }}
                                title="Criar novo workspace"
                                className="text-[11px] font-semibold text-[#004EB1] hover:text-[#003d8d] dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 transition-colors cursor-pointer"
                              >
                                <Plus className="size-3" />
                                <span>Adicionar</span>
                              </button>
                            )}
                          </div>

                          {workspaces.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {workspaces.map((ws) => (
                                <button
                                  key={ws.id}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleEnterAccount(account, ws.slug)
                                  }}
                                  className="group/ws inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-zinc-100/90 dark:bg-zinc-800/80 hover:bg-[#004EB1] hover:text-white dark:hover:bg-[#004EB1] dark:hover:text-white text-zinc-800 dark:text-zinc-200 text-xs font-semibold border border-zinc-200/80 dark:border-zinc-700/60 hover:border-[#004EB1] dark:hover:border-[#004EB1] shadow-xs hover:shadow-md active:scale-[0.98] transition-all duration-200 cursor-pointer"
                                >
                                  <span className="truncate max-w-[150px]">{ws.name}</span>
                                  <ExternalLink className="size-3.5 text-zinc-400 dark:text-zinc-500 group-hover/ws:text-white group-hover/ws:translate-x-0.5 group-hover/ws:-translate-y-0.5 transition-all shrink-0" />
                                </button>
                              ))}
                            </div>
                          ) : (role === "owner" || role === "admin") ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedAccountForNewWs(account)
                                setNewWorkspaceName("")
                                setShowCreateWsModal(true)
                              }}
                              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-[#004EB1]/40 hover:border-[#004EB1] bg-blue-50/50 dark:bg-blue-950/30 hover:bg-blue-50 dark:hover:bg-blue-950/60 text-[#004EB1] dark:text-blue-400 text-xs font-semibold transition-all cursor-pointer shadow-xs"
                            >
                              <Plus className="size-4" />
                              <span>Criar Primeiro Workspace</span>
                            </button>
                          ) : (
                            <span className="text-xs text-zinc-400 dark:text-zinc-500 italic block py-1">
                              Nenhum workspace disponível
                            </span>
                          )}
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            ) : (
              <div className="p-12 text-center rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 space-y-3">
                <Search className="size-8 text-zinc-400 mx-auto" />
                <h3 className="font-bold text-base text-zinc-800 dark:text-zinc-200">{t.noCompaniesFound}</h3>
                <p className="text-xs text-zinc-500">{t.noCompaniesFoundDesc} &quot;{searchQuery}&quot;.</p>
                <Button variant="outline" size="sm" onClick={() => setSearchQuery("")} className="cursor-pointer">
                  {t.clearSearch}
                </Button>
              </div>
            )
          ) : (
            /* 4. Estado Vazio Específico: Usuário sem Empresa */
            <div className="rounded-3xl border-2 border-dashed border-zinc-300 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/40 p-10 sm:p-14 text-center space-y-6 max-w-2xl mx-auto shadow-sm">
              <div className="relative size-20 rounded-3xl bg-[#004EB1]/10 text-[#004EB1] dark:text-blue-400 flex items-center justify-center mx-auto shadow-inner">
                <Building2 className="size-10" />
                <div className="absolute -top-1 -right-1 size-6 rounded-full bg-[#004EB1] text-white flex items-center justify-center text-xs">
                  <Plus className="size-3.5" />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-xl sm:text-2xl font-extrabold text-zinc-900 dark:text-white">
                  {t.noCompaniesLinked}
                </h3>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed max-w-md mx-auto">
                  {t.noCompaniesLinkedDesc}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  onClick={() => {
                    setSwitchAccountInitialMode("create")
                    setIsSwitchAccountOpen(true)
                  }}
                  className="w-full sm:w-auto bg-[#004EB1] hover:bg-[#003d8d] text-white font-semibold text-sm px-6 py-5 rounded-xl shadow-md cursor-pointer flex items-center justify-center gap-2"
                >
                  <Plus className="size-4" />
                  <span>{t.createCompanyBtn}</span>
                </Button>
              </div>

              <div className="pt-4 border-t border-zinc-200/60 dark:border-zinc-800/60 text-xs text-zinc-500 dark:text-zinc-400 flex items-center justify-center gap-2">
                <span>{t.waitingInviteText} <strong>{user?.email}</strong></span>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* 5. Modal de Criar / Alternar Empresa */}
      <SwitchAccountModal
        isOpen={isSwitchAccountOpen}
        onClose={() => {
          setIsSwitchAccountOpen(false)
        }}
        initialMode={switchAccountInitialMode}
        accounts={activeAccounts}
        currentAccount={null}
        hideBackButton={true}
        hideCancelButton={true}
        onSwitchAccount={(accId, wsSlug) => {
          const acc = activeAccounts.find((a) => a.id === accId)
          if (acc) {
            handleEnterAccount(acc, wsSlug)
          } else {
            loadInitialData(false)
          }
        }}
        t={t}
      />

      {/* 6. Modal de Meu Perfil */}
      {isProfileModalOpen && (
        <div className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200">
          <div className="relative w-full max-w-4xl h-[85vh] sm:h-[680px] max-h-[90vh] min-h-[500px] bg-white dark:bg-zinc-900 rounded-3xl border border-zinc-200 dark:border-zinc-800 shadow-2xl flex flex-col overflow-hidden">
            {/* Header do Modal */}
            <div className="flex items-center justify-end px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 shrink-0">
              <button
                onClick={() => setIsProfileModalOpen(false)}
                className="size-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Conteúdo do ProfileView */}
            <div className="flex-1 min-h-0 overflow-hidden">
              <ProfileView
                isMobile={false}
                profileTab={profileTab}
                setProfileTab={setProfileTab}
                t={t}
                user={user}
                profileName={profileName}
                setProfileName={setProfileName}
                profilePhone={profilePhone}
                setProfilePhone={setProfilePhone}
                profileAvatarUrl={profileAvatarUrl}
                setProfileAvatarUrl={setProfileAvatarUrl}
                handleSaveProfile={handleSaveProfile}
                isLoginMusicEnabled={isLoginMusicEnabled}
                setIsLoginMusicEnabled={setIsLoginMusicEnabled}
                loginMusicUrl={loginMusicUrl}
                setLoginMusicUrl={setLoginMusicUrl}
                lang={lang}
                currentPassword={currentPassword}
                setCurrentPassword={setCurrentPassword}
                newPassword={newPassword}
                setNewPassword={setNewPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                isChangingPassword={isChangingPassword}
                handleSavePassword={handleSavePassword}
                totpDisableCode={totpDisableCode}
                setTotpDisableCode={setTotpDisableCode}
                handleDisable2FA={handleDisable2FA}
                handleEnable2FA={handleEnable2FA}
                setupData={setupData}
                totpError={totpError}
                totpCode={totpCode}
                setTotpCode={setTotpCode}
                lockscreenPin={lockscreenPin}
                setLockscreenPin={setLockscreenPin}
                handleCloseApp={() => setIsProfileModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar Workspace na Empresa */}
      {showCreateWsModal && selectedAccountForNewWs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="w-full max-w-md bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden font-sans animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
              <div className="flex items-center gap-2.5">
                <div className="size-8 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-[#004EB1] dark:text-blue-400 flex items-center justify-center">
                  <FolderPlus className="size-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Criar Workspace</h4>
                  <p className="text-[11px] text-zinc-500 truncate max-w-[240px]">
                    Empresa: {selectedAccountForNewWs.name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowCreateWsModal(false)
                  setSelectedAccountForNewWs(null)
                  setNewWorkspaceName("")
                }}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleCreateWorkspaceForAccount} className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                  Nome do Workspace <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  placeholder="Ex: Principal, Vendas, Operações"
                  className="w-full rounded-lg border border-zinc-200 dark:border-zinc-800 px-3.5 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] outline-none"
                />
                <p className="text-[10px] text-zinc-400">
                  O identificador slug será gerado automaticamente a partir do nome.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isCreatingWorkspace}
                  onClick={() => {
                    setShowCreateWsModal(false)
                    setSelectedAccountForNewWs(null)
                    setNewWorkspaceName("")
                  }}
                  className="text-xs h-9 px-4 cursor-pointer font-sans"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={!newWorkspaceName.trim() || isCreatingWorkspace}
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-5 gap-1.5 cursor-pointer shadow-sm font-sans"
                >
                  {isCreatingWorkspace ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      <span>Criando...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="size-3.5" />
                      <span>Criar Workspace</span>
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  )
}
